import React, { Fragment, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';

interface DayOffRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedDate: Date | null;
  onSubmit: (date: Date, reason: string) => void;
}

const DayOffRequestModal: React.FC<DayOffRequestModalProps> = ({
  isOpen,
  onClose,
  selectedDate,
  onSubmit,
}) => {
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedDate || !reason.trim()) return;

    setIsSubmitting(true);
    
    // 실제로는 여기서 API 호출
    await new Promise(resolve => setTimeout(resolve, 500));
    
    onSubmit(selectedDate, reason);
    setReason('');
    setIsSubmitting(false);
    onClose();
  };

  const handleClose = () => {
    setReason('');
    onClose();
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                {/* 헤더 */}
                <div className="flex items-center justify-between mb-4">
                  <Dialog.Title
                    as="h3"
                    className="text-2xl font-bold text-primary"
                  >
                    ✋ 휴무 신청
                  </Dialog.Title>
                  <button
                    type="button"
                    onClick={handleClose}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                {/* 선택된 날짜 표시 */}
                {selectedDate && (
                  <div className="bg-base-cream rounded-lg p-4 mb-6">
                    <p className="text-sm text-gray-600 mb-1">신청 날짜</p>
                    <p className="text-xl font-bold text-primary">
                      {format(selectedDate, 'yyyy년 M월 d일 (EEEE)', { locale: ko })}
                    </p>
                  </div>
                )}

                {/* 신청 폼 */}
                <form onSubmit={handleSubmit}>
                  <div className="mb-6">
                    <label
                      htmlFor="reason"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      신청 사유 <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="reason"
                      rows={4}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-accent focus:outline-none transition-colors resize-none"
                      placeholder="휴무가 필요한 이유를 간단히 적어주세요. (예: 개인 일정, 병원 진료 등)"
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {reason.length}/200자
                    </p>
                  </div>

                  {/* 안내 메시지 */}
                  <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <span className="text-yellow-400 text-xl">⚠️</span>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-yellow-700">
                          인기 날짜는 <strong>아조씨 로또</strong>로 공정하게 추첨됩니다!
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* 버튼 */}
                  <div className="flex space-x-3">
                    <button
                      type="button"
                      onClick={handleClose}
                      className="flex-1 btn-azocsii-outline py-3"
                      disabled={isSubmitting}
                    >
                      취소
                    </button>
                    <button
                      type="submit"
                      className="flex-1 btn-azocsii py-3"
                      disabled={isSubmitting || !reason.trim()}
                    >
                      {isSubmitting ? '신청 중...' : '신청하기'}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default DayOffRequestModal;
